const BLOG_ARTICLES = [
  {
    slug: "mochila-de-vida-emergencia-animales",
    title: "MOCHILA DE VIDA: QUÉ NECESITAS PARA EVACUAR CON TUS ANIMALES",
    subtitle: "La guía definitiva para preparar tu kit de emergencia animal",
    category: "PREPARA",
    categoryColor: "#fa9000",
    readTime: "10 min",
    date: "2026-03-19",
    author: "Emergencia Animal",
    metaDescription: "Guía completa para preparar tu mochila de vida y evacuar con tus animales en una emergencia. Checklist descargable, consejos por tipo de animal y errores comunes.",
    keywords: ["mochila de vida", "kit emergencia animales", "evacuación mascotas", "preparación emergencias", "mochila emergencia perro gato"],
    intro: "Cuando suena la alarma, no hay tiempo para pensar. Cada segundo cuenta, y tus animales dependen de ti. Una mochila de vida bien preparada puede marcar la diferencia entre una evacuación ordenada y el caos. Esta guía te enseña exactamente qué necesitas, por qué lo necesitas, y cómo prepararte para que nadie se quede atrás.",
    related: ["proteger-animales-incendio-forestal", "ola-de-calor-proteger-animales"],
    sections: [
      {
        type: "narrative",
        lead: "El 29 de octubre de 2024, la DANA arrasó Valencia. Miles de familias evacuaron en minutos. Muchas no pudieron llevar a sus animales.",
        content: [
          "Hay una escena que se repitió cientos de veces aquella noche: familias corriendo hacia los coches con lo puesto, mientras sus perros ladraban en el jardín y sus gatos se escondían bajo la cama. Los que tenían todo preparado evacuaron en cinco minutos. Los que no, tuvieron que elegir entre su vida y la de sus animales.",
          "La mochila de vida no es un accesorio de supervivencialismo. Es la herramienta más básica y más efectiva que existe para proteger a tus animales en una emergencia. Tener agua, comida, medicación y documentación preparados de antemano significa que cuando suene la alarma —y algún día sonará—, tú solo tendrás que coger una mochila y salir por la puerta.",
          "Lo que sigue es un protocolo detallado, probado por equipos de rescate animal, para que prepares la tuya hoy."
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1624638760852-8ede1666ab07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxlbWVyZ2VuY3klMjBiYWNrcGFjayUyMHN1cHBsaWVzJTIwa2l0fGVufDB8fHx8MTc4MjQwMTEzMHww&ixlib=rb-4.1.0&q=85",
        alt: "Kit de emergencia abierto con suministros organizados",
        caption: "KIT DE EMERGENCIA — Todo organizado, accesible y listo para coger en segundos. La diferencia entre evacuación y caos.",
        wide: true
      },
      {
        type: "stat",
        bg: "#2D3561",
        items: [
          { value: "5 min", label: "Tiempo para evacuar con mochila preparada", color: "#F5A623" },
          { value: "72%", label: "Familias con animales sin plan de evacuación", color: "#E8412A" },
          { value: "3 días", label: "Autonomía mínima que debe cubrir tu kit", color: "#8B9A2D" }
        ]
      },
      {
        title: "CHECKLIST BÁSICO UNIVERSAL",
        type: "checklist",
        items: [
          { text: "Agua potable (mínimo 3 días: 1 litro/día por animal grande)", essential: true },
          { text: "Comida seca en bolsa hermética (mínimo 5 días)", essential: true },
          { text: "Cuencos plegables de silicona para agua y comida", essential: false },
          { text: "Medicación habitual + receta del veterinario", essential: true },
          { text: "Cartilla sanitaria y microchip actualizado", essential: true },
          { text: "Transportín resistente (1 por animal)", essential: true },
          { text: "Correa extra + arnés de seguridad", essential: false },
          { text: "Manta térmica de emergencia", essential: true },
          { text: "Botiquín animal: gasas, antiséptico, vendas, pinzas", essential: false },
          { text: "Bolsas para residuos y toallitas húmedas", essential: false },
          { text: "Foto reciente impresa del animal con tu nombre y teléfono", essential: true },
          { text: "Juguete o manta con olor familiar (reduce ansiedad)", essential: false }
        ]
      },
      {
        type: "pullquote",
        text: "La diferencia entre una familia que evacúa con sus animales y una que los deja atrás no es el coraje. Es la preparación. Cinco minutos de planificación hoy valen más que cinco horas de angustia mañana.",
        attribution: "Equipos de rescate animal — DANA Valencia 2024",
        color: "#fa9000"
      },
      {
        type: "split",
        left: {
          type: "image",
          src: "https://images.unsplash.com/photo-1712228632561-af007f46a5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkb2clMjBjYXQlMjBwZXQlMjBjYXJyaWVyJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc4MjQwMTEzMHww&ixlib=rb-4.1.0&q=85",
          alt: "Gato dentro de un transportín preparado para evacuar"
        },
        right: {
          type: "text",
          title: "El transportín: tu aliado más importante",
          content: [
            "El mayor error que cometen las familias con gatos es guardar el transportín en el trastero y sacarlo solo para ir al veterinario. Resultado: el gato asocia el transportín con experiencias negativas y, cuando llega la emergencia, se niega a entrar.",
            "Deja el transportín abierto y visible en casa con una manta dentro. Que el gato duerma ahí por voluntad propia. El día que necesites meterlo en cinco minutos con las manos temblando, agradecerás haberlo hecho."
          ]
        }
      },
      {
        title: "ESPECÍFICO POR ANIMAL",
        type: "animals",
        animals: [
          {
            name: "PERROS",
            emoji: "",
            color: "#686af3",
            tips: [
              "Arnés antiescape (no collar) — los perros asustados se zafan del collar en segundos",
              "Bozal de emergencia si tu perro es reactivo bajo estrés",
              "Protectores de almohadillas para terreno caliente o con cristales",
              "Golosinas de alto valor para redirigir atención en pánico",
              "Bolsa con su olor familiar (camiseta tuya usada)"
            ]
          },
          {
            name: "GATOS",
            emoji: "",
            color: "#a88efb",
            tips: [
              "Transportín rígido con puerta superior (facilita la carga bajo estrés)",
              "Spray de feromonas (Feliway) para calmar durante el transporte",
              "Arena portátil y bandeja desechable pequeña",
              "Red o malla de seguridad por si hay que abrir el transportín",
              "Manta para cubrir el transportín (oscuridad = calma)"
            ]
          },
          {
            name: "AVES",
            emoji: "",
            color: "#16a34a",
            tips: [
              "Jaula de transporte pequeña con barras finas",
              "Funda opaca para cubrir la jaula (evita shocks)",
              "Semillas y agua en dispensador antigoteo",
              "Termómetro: las aves son muy sensibles al frío/calor",
              "Nunca dejar en vehículo cerrado, ni un minuto"
            ]
          },
          {
            name: "REPTILES / EXÓTICOS",
            emoji: "",
            color: "#81801a",
            tips: [
              "Bolsa térmica o heat pack para mantener temperatura",
              "Contenedor ventilado y seguro con cierre",
              "Sustrato limpio de repuesto",
              "Alimento vivo o liofilizado según especie",
              "Documentación CITES si aplica (tortugas, iguanas, etc.)"
            ]
          }
        ]
      },
      {
        title: "ERRORES QUE CUESTAN VIDAS",
        type: "colorblock",
        color: "#E8412A",
        content: "Estos son los errores más frecuentes que detectamos en las evacuaciones reales. Cada uno de ellos ha costado la vida de animales que podrían haberse salvado.",
        items: [
          { title: "Preparar la mochila durante la emergencia", desc: "Si la estás haciendo cuando ya suena la alarma, ya llegas tarde. Prepárala HOY, en calma, sin prisas." },
          { title: "Olvidar la medicación", desc: "Sin la medicación habitual o sin copia de la receta, tu animal puede sufrir crisis evitables en un refugio." },
          { title: "Transportín desconocido", desc: "Un gato no entrará en un transportín que no conoce, y menos cuando está aterrorizado. Acostúmbralo antes." },
          { title: "Microchip desactualizado", desc: "Si te has mudado y no has actualizado el teléfono del microchip, nadie podrá devolvértelo si se pierde." },
          { title: "Comida sin bolsa hermética", desc: "La humedad de una inundación o la ceniza de un incendio estropean la comida en horas. Bolsa hermética siempre." },
          { title: "Nunca haber practicado", desc: "El primer simulacro no debe ser la emergencia real. Practica al menos una vez para detectar cuellos de botella." }
        ]
      },
      {
        title: "DÓNDE Y CÓMO GUARDARLA",
        type: "narrative",
        lead: "Una mochila bien preparada pero mal ubicada es una mochila inútil.",
        content: [
          "El lugar ideal es junto a la puerta de salida principal, en un sitio visible y accesible que no requiera mover cajas ni abrir armarios. Si tienes garaje, ten una copia básica en el coche. Revisa el contenido cada tres meses: comprueba fechas de caducidad de comida y medicamentos, actualiza la foto del animal, y asegúrate de que el agua está en buen estado.",
          "Una mochila bien mantenida es una mochila que salva vidas. Una mochila olvidada en el trastero es solo una bolsa con cosas."
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1509762774605-f07235a08f1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxlbWVyZ2VuY3klMjBiYWNrcGFjayUyMHN1cHBsaWVzJTIwa2l0fGVufDB8fHx8MTc4MjQwMTEzMHww&ixlib=rb-4.1.0&q=85",
        alt: "Mochila de emergencia lista al atardecer",
        caption: "PREPARADA Y LISTA — Tu mochila de vida es la primera línea de defensa de tus animales.",
        wide: true
      },
      {
        title: "TU SIGUIENTE PASO",
        type: "callout",
        content: "Tu mochila de vida es la primera línea de defensa de tus animales. No esperes a la próxima emergencia. Prepárala hoy. En Emergencia Animal puedes generar tu checklist personalizado en la sección PREPARA.",
        cta: { label: "IR A MOCHILA DE VIDA", href: "/prepara/mochila" }
      }
    ]
  },
  {
    slug: "proteger-animales-incendio-forestal",
    title: "CÓMO PROTEGER A LOS ANIMALES EN UN INCENDIO FORESTAL",
    seoTitle: "Cómo proteger a los animales en un incendio forestal: guía práctica para antes, durante y después",
    subtitle: "Guía práctica para actuar con calma antes, durante y después",
    category: "EMERGENCIAS",
    categoryColor: "#f64e0e",
    readTime: "10 min",
    date: "2026-03-19",
    author: "Emergencia Animal",
    metaDescription: "Aprende cómo proteger a los animales ante un incendio forestal con recomendaciones claras y fáciles de aplicar. Preparación, evacuación y cuidados en cada fase.",
    keywords: ["incendio forestal animales", "proteger animales incendio", "evacuar animales de compañía", "preparación incendio forestal", "kit de emergencia animales"],
    intro: "En los incendios forestales, la atención suele centrarse en las personas, las viviendas y el entorno natural. Sin embargo, los animales también forman parte de estas situaciones y, en muchos casos, dependen completamente de quienes los cuidan para estar a salvo. Saber cómo actuar con ellos no solo mejora su protección, sino que también ayuda a reducir la incertidumbre y a tomar decisiones con más calma cuando todo sucede deprisa.",
    related: ["mochila-de-vida-emergencia-animales", "ola-de-calor-proteger-animales"],
    sections: [
      {
        type: "narrative",
        lead: "La buena noticia: no hace falta esperar a una emergencia para empezar a hacer las cosas mejor.",
        content: [
          "Igual que ocurre con otros aspectos de la prevención, una parte importante de la seguridad se construye antes de que exista un peligro inmediato. Pensar hoy en cómo actuaríamos permite hacerlo mañana con más serenidad, si alguna vez hace falta.",
          "Esta guía recorre las tres fases de un incendio forestal —antes, durante y después— con recomendaciones claras y fáciles de aplicar. No se trata de hacerlo todo perfecto, sino de dar pequeños pasos que, llegado el momento, facilitan decisiones más seguras."
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1615092296061-e2ccfeb2f3d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHx3aWxkZmlyZSUyMGZvcmVzdCUyMGZpcmUlMjBlbWVyZ2VuY3l8ZW58MHx8fHwxNzgyMjYxMTc4fDA&ixlib=rb-4.1.0&q=85",
        alt: "Incendio forestal en una ladera al atardecer",
        caption: "El fuego es imprevisible, pero la preparación no: gran parte de la seguridad se construye con antelación y con calma.",
        wide: true
      },
      {
        type: "stat",
        bg: "#2D3561",
        items: [
          { value: "3", label: "Momentos clave: antes, durante y después", color: "#F5A623" },
          { value: "1", label: "Kit básico con lo esencial en un mismo lugar", color: "#686af3" },
          { value: "2", label: "Rutas de salida alternativas pensadas con tiempo", color: "#8B9A2D" }
        ]
      },
      {
        title: "PREPARARSE ANTES DEL INCENDIO",
        type: "narrative",
        lead: "Pequeños pasos que ayudan mucho.",
        content: [
          "Cuando hablamos de incendios forestales, la preparación no consiste en hacer planes complejos, sino en tomar algunas decisiones sencillas que facilitan mucho la respuesta si alguna vez hay que actuar. Por ejemplo, pensar con antelación quién se encargará de cada animal, qué rutas se podrían utilizar para salir o a qué lugar se podría acudir temporalmente permite evitar improvisaciones en un momento de tensión.",
          "También resulta útil tener localizados los elementos básicos: correas, arneses, transportines o jaulas. Son objetos cotidianos, pero en una evacuación rápida pueden convertirse en esenciales. Además, acostumbrar a los animales al transportín o al vehículo, aunque sea de forma progresiva y tranquila, puede marcar una gran diferencia: lo que para nosotros es una acción urgente, para ellos puede ser algo completamente nuevo y desconcertante."
        ]
      },
      {
        type: "pullquote",
        text: "Preparar estas pequeñas cosas no implica anticipar lo peor, sino ganar serenidad si alguna vez hace falta reaccionar con rapidez.",
        attribution: "Emergencia Animal",
        color: "#8B9A2D"
      },
      {
        type: "split",
        left: {
          type: "image",
          src: "https://images.unsplash.com/photo-1648809644635-951fa3c27440?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxkb2clMjByZXNjdWUlMjBlbWVyZ2VuY3klMjBldmFjdWF0aW9ufGVufDB8fHx8MTc4MjI2MTE3OHww&ixlib=rb-4.1.0&q=85",
          alt: "Persona caminando tranquilamente con su perro"
        },
        right: {
          type: "text",
          title: "Un kit de emergencia: orden en momentos de incertidumbre",
          content: [
            "Otro recurso sencillo que suele resultar muy útil es preparar un pequeño kit de emergencia para los animales. No se trata de hacer algo perfecto, sino de reunir en un mismo lugar lo básico para no tener que buscarlo todo en el último momento: agua, alimento, medicación, documentación, sistemas de sujeción y algún objeto familiar.",
            "En muchos casos, lo que más estrés genera no es solo la situación, sino la sensación de no encontrar lo necesario. Tener este kit preparado aporta tranquilidad incluso si nunca llega a utilizarse, porque significa que, llegado el caso, todo está un poco más bajo control."
          ]
        }
      },
      {
        title: "ANTES DEL INCENDIO",
        type: "colorblock",
        color: "#8B9A2D",
        icon: true,
        content: "La preparación no consiste en hacer planes complejos, sino en decisiones sencillas que facilitan la respuesta. Cada uno de estos pasos suma tranquilidad.",
        items: [
          { title: "Quién se encarga de cada animal", desc: "Decidir con antelación quién se ocupará de cada uno evita improvisaciones en un momento de tensión. Es una conversación de cinco minutos que aporta mucha claridad." },
          { title: "Rutas y lugares de acogida", desc: "Pensar qué caminos se podrían utilizar para salir y a qué lugar se podría acudir temporalmente. Recorrerlos alguna vez con calma ayuda a conocerlos mejor." },
          { title: "Elementos básicos localizados", desc: "Correas, arneses, transportines o jaulas. Son objetos cotidianos, pero tenerlos a mano facilita mucho una salida ordenada." },
          { title: "Kit de emergencia", desc: "Agua, alimento, medicación, documentación y algún objeto familiar en un mismo lugar. Aporta tranquilidad incluso si nunca llega a usarse." },
          { title: "Transportín como espacio conocido", desc: "Acostumbrar a los animales al transportín y al vehículo de forma progresiva y tranquila hace que, si algún día es necesario, no sea algo desconcertante para ellos." },
          { title: "Microchip y documentación al día", desc: "Revisar que el microchip está registrado con un teléfono actual y guardar una copia de la cartilla en el móvil facilita el reencuentro si os separáis." },
          { title: "Cada especie, su preparación", desc: "Aves, pequeños mamíferos o reptiles necesitan recipientes adecuados. Para caballos o animales en explotaciones, conviene pensar antes en el transporte y el destino." },
          { title: "Ensayo sin prisa", desc: "Practicar la salida alguna vez, sin urgencia y en un ambiente tranquilo, permite descubrir qué se puede mejorar con tiempo de sobra." }
        ]
      },
      {
        type: "image",
        src: "https://images.pexels.com/photos/7155806/pexels-photo-7155806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        alt: "Bombero atendiendo a un gato con cuidado",
        caption: "Los equipos de emergencia cuentan con protocolos para animales. Avisar de su presencia facilita mucho su trabajo.",
        wide: false
      },
      {
        title: "DURANTE EL INCENDIO",
        type: "narrative",
        lead: "Actuar con calma y con criterio.",
        content: [
          "Cuando se produce un incendio o hay una alerta cercana, es normal que aparezca la sensación de urgencia. En esos momentos, haber pensado antes algunos aspectos puede ayudar a actuar de forma más organizada.",
          "Si existe margen suficiente para evacuar, suele ser preferible hacerlo con tiempo, manteniendo a los animales controlados y utilizando los medios de sujeción adecuados. Salir de forma ordenada, con lo esencial preparado, reduce tanto el riesgo como el estrés.",
          "En cambio, si no es posible evacuar de inmediato, lo más importante es centrarse en minimizar riesgos dentro del entorno disponible: mantener a los animales localizados, evitar que se dispersen y buscar una zona lo más segura posible. Cada incendio es diferente, pero incluso en contextos complejos sigue siendo posible tomar decisiones prudentes que mejoren la seguridad."
        ]
      },
      {
        title: "DURANTE EL INCENDIO",
        type: "colorblock",
        color: "#f64e0e",
        content: "Haber pensado antes algunos aspectos ayuda a actuar de forma más organizada. Estas pautas sencillas facilitan una salida más segura y tranquila.",
        items: [
          { title: "Salir con tiempo, si es posible", desc: "Cuando hay margen para evacuar, hacerlo pronto y de forma ordenada reduce el riesgo y el estrés, tanto para las personas como para los animales." },
          { title: "Animales controlados y sujetos", desc: "Utilizar correa, arnés o transportín durante toda la salida. En una situación nueva, incluso los animales más tranquilos agradecen sentirse guiados." },
          { title: "Primero quienes necesitan más tiempo", desc: "Gatos y animales pequeños suelen tardar más en estar listos. Empezar por ellos, con suavidad —una toalla puede ayudar a envolver a un gato asustado—, facilita el resto." },
          { title: "Si no es posible salir de inmediato", desc: "Mantener a los animales localizados, evitar que se dispersen y buscar la zona más segura del entorno disponible. Seguir las indicaciones oficiales en todo momento." },
          { title: "Atención al humo", desc: "Mantener a los animales en interiores el mayor tiempo posible, reducir la actividad y ofrecer agua con frecuencia. Un paño húmedo puede ayudar a proteger sus vías respiratorias." },
          { title: "Animales grandes o en explotaciones", desc: "Es más seguro abrir puertas y vallas hacia zonas despejadas que intentar transportarlos en vehículos no preparados. Avisar de su presencia a los equipos de emergencia." },
          { title: "Si algún animal queda atrás", desc: "La mejor ayuda es llamar al 112, indicar la ubicación exacta y cuántos animales hay. Los equipos de emergencia disponen de medios y protocolos para actuar." }
        ]
      },
      {
        type: "pullquote",
        text: "El humo también es importante: incluso sin llamas visibles, conviene adaptar la rutina, reducir la actividad y ofrecer agua con frecuencia.",
        attribution: "Emergencia Animal",
        color: "#2D3561"
      },
      {
        title: "EL HUMO TAMBIÉN ES IMPORTANTE",
        type: "narrative",
        content: [
          "A veces tendemos a pensar en el fuego como el principal problema, pero el humo también puede afectar de forma importante a los animales. Incluso sin llamas visibles, una mala calidad del aire puede provocar irritación, dificultades respiratorias o malestar general, especialmente en animales más sensibles.",
          "Por eso, cuando el ambiente está cargado de humo, conviene adaptar la rutina: mantener a los animales en interiores el mayor tiempo posible, reducir paseos y actividad física, y ofrecer agua con frecuencia. Estos cambios son sencillos, pero pueden ayudar a reducir el impacto del humo en su salud."
        ]
      },
      {
        type: "split",
        reverse: true,
        left: {
          type: "text",
          title: "Cada animal es diferente",
          content: [
            "No todos los animales reaccionan igual ante una situación de emergencia. Un perro o un gato habituados al manejo pueden adaptarse relativamente bien si se actúa con calma, pero otros animales requieren una preparación más específica. Las aves, los pequeños mamíferos o los reptiles necesitan recipientes adecuados y un entorno estable.",
            "En el caso de caballos o animales en explotaciones, la planificación debe empezar antes y contemplar aspectos como el transporte o el lugar de destino. Adaptar las decisiones al tipo de animal no complica las cosas; al contrario, facilita que la respuesta sea más adecuada y reduce problemas en el momento clave."
          ]
        },
        right: {
          type: "image",
          src: "https://images.pexels.com/photos/11538211/pexels-photo-11538211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          alt: "Caballos pastando tranquilamente en un campo abierto"
        }
      },
      {
        title: "DESPUÉS DEL INCENDIO",
        type: "narrative",
        lead: "Volver poco a poco.",
        content: [
          "Cuando el incendio ha pasado, puede parecer que todo vuelve a la normalidad, pero el entorno puede seguir siendo inestable durante un tiempo. Cenizas, objetos dañados o cambios en el espacio pueden afectar tanto a la seguridad como al comportamiento de los animales, que a veces se muestran desorientados o más sensibles de lo habitual.",
          "Por eso, es recomendable retomar la rutina de forma gradual, supervisando cómo se encuentran, revisando su estado y evitando que deambulen sin control nada más regresar. La recuperación también forma parte del cuidado, y hacerlo con calma ayuda a que todo vuelva a su sitio poco a poco."
        ]
      },
      {
        title: "DESPUÉS DEL INCENDIO",
        type: "colorblock",
        color: "#a88efb",
        content: "La recuperación también forma parte del cuidado. Volver con calma ayuda a que todo regrese a su sitio poco a poco.",
        items: [
          { title: "Regresar con la indicación oficial", desc: "Esperar a que las autoridades confirmen que la zona es segura. El entorno puede tardar un tiempo en estabilizarse del todo." },
          { title: "Revisar el entorno con calma", desc: "Antes de dejar a los animales moverse con libertad, conviene comprobar el estado del espacio: cenizas, objetos dañados o cambios que puedan afectarles." },
          { title: "Retomar la rutina de forma gradual", desc: "Paseos más cortos, horarios tranquilos y tiempo para readaptarse. Cada animal tiene su propio ritmo." },
          { title: "Observar cómo se encuentran", desc: "Es normal que durante unos días se muestren desorientados o más sensibles de lo habitual. Acompañarlos con paciencia forma parte de la recuperación." },
          { title: "Consultar al veterinario si hay dudas", desc: "Si aparecen tos, cansancio o malestar tras la exposición al humo, una revisión veterinaria ayuda a quedarse tranquilo." },
          { title: "Evitar que deambulen sin control", desc: "Los primeros días es preferible mantenerlos acompañados y en espacios conocidos, hasta que el entorno vuelva a ser el de siempre." }
        ]
      },
      {
        title: "RECUERDA",
        type: "colorblock",
        color: "#2D3561",
        content: "Algunas ideas sencillas que ayudan a decidir con más tranquilidad en cualquier fase.",
        items: [
          { title: "Pedir ayuda es actuar bien", desc: "El 112 está para eso. Avisar de la presencia de animales, con la ubicación exacta, es una de las formas más eficaces de protegerlos." },
          { title: "La calma también se prepara", desc: "Los animales perciben nuestro estado de ánimo. Actuar con serenidad les ayuda a ellos tanto como a nosotros." },
          { title: "Agua templada en quemaduras", desc: "Si un animal presenta quemaduras, el agua templada es más adecuada que la fría, y la valoración veterinaria, siempre recomendable." },
          { title: "La información oficial es la guía", desc: "Seguir los canales oficiales (112, Protección Civil, ES-Alert) ayuda a tomar decisiones adecuadas en cada momento." }
        ]
      },
      {
        title: "TELÉFONOS DE EMERGENCIA",
        type: "phones",
        phones: [
          { number: "112", label: "Emergencias generales" },
          { number: "062", label: "Guardia Civil (SEPRONA)" },
          { number: "080", label: "Bomberos" },
          { number: "1006", label: "Protección Civil" }
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1511027643875-5cbb0439c8f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx3aWxkZmlyZSUyMGZvcmVzdCUyMGZpcmUlMjBlbWVyZ2VuY3l8ZW58MHx8fHwxNzgyMjYxMTc4fDA&ixlib=rb-4.1.0&q=85",
        alt: "Bosque durante un incendio nocturno",
        caption: "Mantenerse informado a través de los canales oficiales ayuda a decidir con calma en cada fase del incendio.",
        wide: true
      },
      {
        title: "UNA IDEA IMPORTANTE PARA LLEVARSE",
        type: "callout",
        content: "En una emergencia es normal sentir nervios o incertidumbre, especialmente si no se ha vivido una situación así antes. Precisamente por eso, preparar algunos elementos básicos con antelación puede marcar una gran diferencia. No se trata de hacerlo perfecto, sino de facilitar decisiones más seguras cuando el tiempo es limitado. Incluir a los animales en los planes familiares es una forma sencilla de cuidarlos, y también de cuidar a toda la unidad familiar. A veces, los pasos más pequeños son los que más ayudan cuando la situación se complica.",
        cta: { label: "CREAR MI PLAN DE EMERGENCIA", href: "/prepara/plan" }
      }
    ]
  },
  {
    slug: "ola-de-calor-proteger-animales",
    title: "OLA DE CALOR: CÓMO PROTEGER A TUS ANIMALES DEL GOLPE DE CALOR",
    subtitle: "Síntomas, primeros auxilios y protocolo de prevención por especie",
    category: "EMERGENCIAS",
    categoryColor: "#f64e0e",
    readTime: "12 min",
    date: "2026-03-19",
    author: "Emergencia Animal",
    metaDescription: "Guía para proteger a tus animales durante olas de calor. Síntomas del golpe de calor en perros y gatos, primeros auxilios, errores fatales y prevención. Información vital.",
    keywords: ["golpe de calor animales", "ola de calor mascotas", "proteger perro calor", "golpe de calor perro síntomas", "emergencia calor animales"],
    intro: "Cada verano mueren cientos de animales en España por golpes de calor evitables. Un perro puede morir en 15 minutos dentro de un coche a 35°C. Los animales no sudan como nosotros — su sistema de regulación térmica es mucho más limitado. Conocer los síntomas y actuar rápido es literalmente cuestión de vida o muerte.",
    related: ["proteger-animales-incendio-forestal", "pirotecnia-proteger-animales"],
    sections: [
      {
        type: "narrative",
        lead: "En 2023, España superó los 45°C. Las olas de calor serán cada vez más frecuentes. Y tus animales no pueden decirte que tienen calor.",
        content: [
          "El calor mata en silencio. Un perro dejado en un coche 'solo diez minutos' mientras su dueño entra al supermercado puede estar muerto cuando vuelva. Un gato encerrado en un ático sin ventilación puede colapsar sin que nadie se dé cuenta. Un conejo en una terraza al sol directo puede sufrir un fallo orgánico irreversible en menos de una hora.",
          "Lo que hace al golpe de calor especialmente peligroso es que los síntomas iniciales son sutiles. Un jadeo ligeramente más rápido. Un poco más de babeo de lo normal. Para cuando los síntomas son evidentes —tambaleo, encías azuladas, colapso—, el daño interno ya puede ser irreversible. El cerebro, el hígado y los riñones empiezan a fallar a partir de los 41°C de temperatura corporal.",
          "Este artículo te enseña a reconocer las señales antes de que sea demasiado tarde, a actuar correctamente en los primeros minutos críticos, y a prevenir que tu animal llegue nunca a esa situación."
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1663501258049-77f9638cbaea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxkb2clMjBzdW1tZXIlMjBoZWF0JTIwcGFudGluZyUyMHdhdGVyfGVufDB8fHx8MTc4MjQwMTEzMHww&ixlib=rb-4.1.0&q=85",
        alt: "Perro bebiendo agua de un grifo en verano",
        caption: "HIDRATACIÓN VITAL — El agua fresca y accesible es la primera barrera contra el golpe de calor.",
        wide: true
      },
      {
        type: "stat",
        bg: "#2D3561",
        items: [
          { value: "15 min", label: "Tiempo para que un perro muera en un coche a 35°C", color: "#E8412A" },
          { value: "41°C", label: "Temperatura corporal donde empiezan los daños orgánicos", color: "#F5A623" },
          { value: "50°C", label: "Temperatura interior de un coche al sol en 10 minutos", color: "#f64e0e" }
        ]
      },
      {
        title: "CÓMO REGULA EL CALOR TU ANIMAL",
        type: "animals",
        animals: [
          {
            name: "PERROS", emoji: "", color: "#686af3",
            tips: ["Jadean para bajar temperatura — si el jadeo es excesivo, es señal de alerta", "Razas braquicéfalas (bulldog, pug, bóxer) son las más vulnerables", "Las almohadillas se queman con el asfalto caliente a partir de 35°C", "Perros obesos, mayores o con problemas cardíacos tienen mayor riesgo", "Nunca rapar el pelo pensando que pasarán menos calor — el pelo protege del sol"]
          },
          {
            name: "GATOS", emoji: "", color: "#a88efb",
            tips: ["Buscan instintivamente superficies frías (baldosas, sombras)", "Gatos blancos y de pelo corto son más sensibles a quemaduras solares", "La deshidratación en gatos es silenciosa — vigila que beban", "Si el gato babea de forma inusual, puede ser golpe de calor", "Los gatos persas y exóticos (cara chata) tienen mayor riesgo"]
          },
          {
            name: "CONEJOS Y PEQUEÑOS MAMÍFEROS", emoji: "", color: "#e8637a",
            tips: ["Son EXTREMADAMENTE sensibles al calor (a partir de 28°C sufren)", "No pueden jadear ni sudar — las orejas son su radiador natural", "Si sus orejas están muy calientes y rojas: emergencia", "Nunca los tengas al sol directo, ni siquiera en balcones", "Coloca baldosas de cerámica frías en su espacio"]
          }
        ]
      },
      {
        type: "pullquote",
        text: "El jadeo excesivo no es 'normal en verano'. Es la primera señal de que tu animal está luchando por regular su temperatura. Si además ves encías rojas o babeo espeso, tienes minutos para actuar.",
        attribution: "Colegio Oficial de Veterinarios de España",
        color: "#E8412A"
      },
      {
        title: "SÍNTOMAS DEL GOLPE DE CALOR",
        type: "colorblock",
        color: "#f64e0e",
        content: "El golpe de calor progresa rápido. Conocer la secuencia te permite actuar en el momento correcto.",
        items: [
          { title: "Jadeo excesivo y rápido", desc: "En perros es la primera señal. En gatos, si jadean con la boca abierta, es SIEMPRE emergencia." },
          { title: "Babeo espeso o espumoso", desc: "La saliva se vuelve densa y viscosa. Señal de deshidratación avanzada." },
          { title: "Encías rojas o azuladas", desc: "Rojo intenso = sobrecalentamiento. Azuladas o pálidas = fallo circulatorio." },
          { title: "Tambaleo y desorientación", desc: "No puede caminar recto, choca con objetos. El cerebro está sufriendo daño por calor." },
          { title: "Vómitos o diarrea", desc: "A veces con sangre. Los órganos internos están empezando a fallar." },
          { title: "Colapso y convulsiones", desc: "EMERGENCIA ABSOLUTA. Aplica primeros auxilios mientras llamas al veterinario de urgencias." }
        ]
      },
      {
        type: "split",
        reverse: true,
        left: {
          type: "text",
          title: "Primeros auxilios: los 5 minutos que salvan vidas",
          content: [
            "Lo primero: lleva al animal a la sombra inmediatamente. Mójalo con agua TEMPLADA (nunca fría — causa vasoconstricción y empeora la situación). Aplica paños húmedos en ingles, axilas, cuello y almohadillas.",
            "Ofrece agua fresca sin forzar la ingesta. Ventílalo con abanico o ventilador. Y llama al veterinario de urgencias — las lesiones internas pueden manifestarse horas después."
          ]
        },
        right: {
          type: "image",
          src: "https://images.pexels.com/photos/37400230/pexels-photo-37400230.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          alt: "Gato descansando a la sombra en un jardín"
        }
      },
      {
        title: "LO QUE NUNCA DEBES HACER",
        type: "colorblock",
        color: "#2D3561",
        content: "Estos errores son más comunes de lo que piensas. Cada uno de ellos ha causado muertes evitables.",
        items: [
          { title: "Dejar un animal en el coche", desc: "JAMÁS. Ni 'solo un momento'. En 10 minutos el interior alcanza 50°C." },
          { title: "Pasear en horas centrales", desc: "Entre las 12:00 y las 17:00 el asfalto supera los 60°C. Las almohadillas se queman." },
          { title: "Usar hielo directo", desc: "El hielo sobre la piel causa vasoconstricción — el cuerpo retiene más calor." },
          { title: "Obligar a beber agua", desc: "Un animal desorientado puede aspirar agua a los pulmones." },
          { title: "Rapar perros de doble capa", desc: "Su doble capa de pelo AÍSLA del calor. Raparlos los deja más expuestos." },
          { title: "Pensar 'ya se le pasará'", desc: "El golpe de calor es mortal sin tratamiento. Los órganos fallan en minutos." }
        ]
      },
      {
        title: "PREVENCIÓN: 10 REGLAS DE ORO",
        type: "checklist",
        items: [
          { text: "Agua fresca y limpia disponible 24/7, en varios puntos de la casa", essential: true },
          { text: "Paseos temprano (antes de las 9h) y al atardecer (después de las 20h)", essential: true },
          { text: "Test del asfalto: si no puedes mantener tu mano 5 segundos, está demasiado caliente", essential: true },
          { text: "Sombra permanente si el animal vive en exterior", essential: true },
          { text: "Esterilla refrigerante o baldosas para que se tumbe", essential: false },
          { text: "Añadir cubitos de hielo al agua (pero no directamente sobre el animal)", essential: false },
          { text: "Crema solar especial para mascotas en orejas y nariz rosadas", essential: false },
          { text: "Piscina inflable pequeña para que se refresque", essential: false },
          { text: "Reducir ejercicio intenso — juegos tranquilos en interior", essential: true },
          { text: "Vigilar especialmente a animales mayores, cachorros y razas braquicéfalas", essential: true }
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1567954363518-dec8219a176d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxkb2clMjBzdW1tZXIlMjBoZWF0JTIwcGFudGluZyUyMHdhdGVyfGVufDB8fHx8MTc4MjQwMTEzMHww&ixlib=rb-4.1.0&q=85",
        alt: "Golden retriever refrescándose en el agua",
        caption: "REFRESCARSE — El agua es el mejor aliado contra el calor. Piscinas, fuentes, mangueras: todo vale.",
        wide: true
      },
      {
        title: "PREPÁRATE PARA ESTE VERANO",
        type: "callout",
        content: "Las olas de calor serán cada vez más frecuentes e intensas por el cambio climático. España superó los 45°C en 2023. Estar preparado no es una opción — es una responsabilidad con quienes no pueden cuidarse solos.",
        cta: { label: "VER ALERTAS METEOROLÓGICAS", href: "/tiempo" }
      }
    ],
    downloadables: [
      {
        title: "Guía práctica: Golpe de calor y animales",
        desc: "Infografía completa con señales de alarma, primeros auxilios y 10 consejos de prevención. Ideal para imprimir y compartir.",
        url: "https://customer-assets.emergentagent.com/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/tn61op9r_EA_Guia_C%C3%B3mo%20prevenir%20el%20golpe%20de%20calor%20en%20animales%20.pdf",
        type: "PDF",
        size: "Guía práctica",
      }
    ],
  },
  {
    slug: "pirotecnia-proteger-animales",
    title: "PIROTECNIA Y ANIMALES: CÓMO PROTEGERLOS DE LOS FUEGOS ARTIFICIALES",
    subtitle: "Prevención, señales de alarma y protocolo de actuación para fiestas y celebraciones",
    category: "EMERGENCIAS",
    categoryColor: "#a88efb",
    readTime: "9 min",
    date: "2026-06-23",
    author: "Emergencia Animal",
    metaDescription: "Guía para proteger a tus animales de la pirotecnia y fuegos artificiales. 10 consejos de prevención, señales de alarma y qué hacer durante las fiestas.",
    keywords: ["pirotecnia animales", "fuegos artificiales mascotas", "proteger perro petardos", "miedo petardos perro", "fiestas animales protección"],
    intro: "Los fuegos artificiales y petardos generan ruido intenso, vibraciones, destellos y olor a pólvora. Para muchos animales, cada detonación es una bomba sin explicación posible. Lo que para nosotros es una celebración, para ellos es un bombardeo impredecible que activa sus instintos más primitivos de huida y supervivencia.",
    related: ["ola-de-calor-proteger-animales", "mochila-de-vida-emergencia-animales"],
    sections: [
      {
        type: "narrative",
        lead: "Cada Nochevieja, cada feria, cada fiesta patronal, cientos de animales se pierden, se lesionan o mueren por la pirotecnia.",
        content: [
          "La imagen se repite todos los años en los refugios de toda España: la mañana del 1 de enero, las protectoras reciben decenas de llamadas. Perros que saltaron vallas de dos metros en plena noche. Gatos que atravesaron mosquiteras para huir del estruendo. Aves encontradas muertas en parques tras sufrir paros cardíacos. Un perro de caza que reventó su cadena y apareció a 30 kilómetros de su casa, deshidratado, con las almohadillas destrozadas por el asfalto.",
          "No son casos excepcionales. Son la norma. Según datos del Colegio de Veterinarios de Madrid, las urgencias veterinarias aumentan hasta un 40% durante los períodos de pirotecnia intensa. Y lo más trágico es que la mayoría de estas emergencias son completamente evitables.",
          "Un perro oye frecuencias que nosotros ni detectamos. Su rango auditivo alcanza los 65.000 Hz — el nuestro se queda en 20.000 Hz. Lo que para ti es un petardo lejano, para tu perro es una explosión directa. Los gatos, las aves y los pequeños mamíferos tienen una sensibilidad auditiva similar o superior. Entender esto no es empatía — es biología. Y cambia completamente cómo debemos actuar."
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1549534077-1b4f31dd59ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxmaXJld29ya3MlMjBuaWdodCUyMGNpdHklMjBjZWxlYnJhdGlvbnxlbnwwfHx8fDE3ODI0MTExMzZ8MA&ixlib=rb-4.1.0&q=85",
        alt: "Fuegos artificiales iluminando el cielo nocturno sobre una ciudad",
        caption: "CELEBRACIÓN HUMANA, EMERGENCIA ANIMAL — Lo que para nosotros es fiesta, para millones de animales es una noche de terror impredecible.",
        wide: true
      },
      {
        type: "stat",
        bg: "#2D3561",
        items: [
          { value: "40%", label: "Aumento de urgencias veterinarias en noches de pirotecnia", color: "#a88efb" },
          { value: "65.000 Hz", label: "Rango auditivo canino (humanos: 20.000 Hz)", color: "#F5A623" },
          { value: "1 de cada 5", label: "Perros que se pierden durante episodios de pirotecnia", color: "#E8412A" }
        ]
      },
      {
        title: "QUÉ LE PASA A TU ANIMAL",
        type: "narrative",
        lead: "No es miedo. Es pánico fisiológico. Y tiene consecuencias médicas.",
        content: [
          "Cuando un petardo estalla, el cuerpo del animal activa la respuesta de lucha o huida. La adrenalina se dispara, el corazón se acelera, los músculos se tensan para correr. Pero no hay adónde huir. El ruido llega de todas partes, rebota en las paredes, vibra en el suelo. El animal no puede procesar el origen de la amenaza, así que su cuerpo sigue descargando cortisol y adrenalina sin parar. El resultado es un estado de estrés agudo que puede durar horas.",
          "Los signos son inequívocos: temblores, jadeo excesivo, hipersalivación, pupilas dilatadas, intentos de escape desesperados, vocalizaciones o, al contrario, parálisis total. Algunos animales pierden el control de esfínteres, otros vomitan. En casos extremos — especialmente en animales mayores, con patologías cardíacas o con historial de fobia —, el estrés puede provocar un colapso cardiovascular. No es una exageración: es una urgencia veterinaria documentada."
        ]
      },
      {
        type: "pullquote",
        text: "Un perro con fobia al ruido no se 'acostumbra'. Cada exposición sin tratamiento refuerza el trauma y agrava la respuesta. La desensibilización requiere meses de trabajo profesional, no noches de fuegos artificiales.",
        attribution: "Colegio Oficial de Veterinarios de Madrid",
        color: "#a88efb"
      },
      {
        type: "split",
        left: {
          type: "image",
          src: "https://images.unsplash.com/photo-1530834673170-b9d8c4f7d3b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxzY2FyZWQlMjBkb2clMjBoaWRpbmclMjBibGFua2V0JTIwYW54aWV0eXxlbnwwfHx8fDE3ODI0MTExMzV8MA&ixlib=rb-4.1.0&q=85",
          alt: "Perro escondiéndose bajo una manta por miedo al ruido"
        },
        right: {
          type: "text",
          title: "El refugio seguro: tu mejor herramienta",
          content: [
            "El concepto es simple: antes de que empiece el ruido, prepara una habitación interior de la casa —sin ventanas a la calle si es posible— con la cama o manta favorita de tu animal, agua fresca, algún juguete y una fuente de sonido suave (música clásica, ruido blanco, o simplemente el televisor encendido).",
            "No obligues al animal a quedarse allí, pero déjale la puerta abierta para que entre por su cuenta. La mayoría elegirá ese espacio instintivamente. Un perro que tiene un refugio seguro al que acudir no necesita escapar de casa. Esa diferencia puede salvarle la vida."
          ]
        }
      },
      {
        title: "ANTES DE LA PIROTECNIA",
        type: "colorblock",
        color: "#8B9A2D",
        content: "La preparación se hace en calma, días antes. Cuando empiece el ruido, ya es tarde para planificar.",
        items: [
          { title: "Prepara el refugio seguro", desc: "Habitación interior, puerta abierta, manta familiar, agua y música suave. Que el animal lo conozca antes de necesitarlo." },
          { title: "Paseos adelantados", desc: "Saca a los perros a pasear horas antes del horario previsto de petardos. Siempre con correa y arnés antiescape — los perros asustados se zafan del collar." },
          { title: "Ventanas y persianas cerradas", desc: "Cierra todo antes de que empiece. Reduce el impacto sonoro y los destellos de luz. Evita que los gatos puedan saltar al exterior." },
          { title: "Microchip y contacto actualizado", desc: "Si tu animal se escapa, el microchip es la única vía de reuniros. Comprueba que el teléfono asociado es el correcto." },
          { title: "No dejes al animal solo", desc: "Si sabes que habrá pirotecnia, organízate para estar en casa. Tu presencia tranquila es el ansiolítico más efectivo que existe." },
          { title: "Consulta veterinaria preventiva", desc: "Si tu animal tiene historial de fobia al ruido o patologías cardíacas, habla con tu veterinario con antelación. Hay tratamientos que ayudan — pero necesitan tiempo." }
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1581888227599-779811939961?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxkb2clMjBjb21mb3J0JTIwc2FmZSUyMHJvb20lMjBjb3p5JTIwcGV0fGVufDB8fHx8MTc4MjQxMTEzNXww&ixlib=rb-4.1.0&q=85",
        alt: "Perro descansando en un espacio seguro y confortable",
        caption: "REFUGIO SEGURO — Un espacio interior conocido, con objetos familiares, reduce drásticamente la respuesta de pánico.",
        wide: true
      },
      {
        title: "DURANTE LOS FUEGOS ARTIFICIALES",
        type: "narrative",
        lead: "Han empezado los petardos. Tú estás preparado. Esto es lo que haces.",
        content: [
          "Lo primero: mantén la calma. Los animales leen tu lenguaje corporal con una precisión que no imaginas. Si tú estás tenso, nervioso o sobreproteges al animal con abrazos constantes, le estás confirmando que hay una amenaza real. Actúa con naturalidad. Siéntate cerca, habla con voz suave, y deja que el animal gestione el miedo a su ritmo.",
          "Si tu perro o gato busca esconderse debajo de la cama, dentro de un armario, detrás del sofá — déjale. Ese es su mecanismo de afrontamiento. No lo saques de ahí, no lo fuerces a enfrentarse al ruido, no intentes distraerlo con juegos forzados. Lo mejor que puedes hacer es quedarte cerca, sentarte en la misma habitación, y dejar que sienta tu presencia sin agobio."
        ]
      },
      {
        title: "PROTOCOLO DURANTE LA PIROTECNIA",
        type: "colorblock",
        color: "#a88efb",
        content: "Los petardos ya están sonando. Este es tu protocolo paso a paso para minimizar el sufrimiento de tu animal.",
        items: [
          { title: "Acompaña sin forzar", desc: "Quédate cerca, habla con voz suave, pero no saques al animal de su escondite ni lo abraces compulsivamente. Tu calma es su ancla." },
          { title: "Espacio interior oscuro y tranquilo", desc: "Si el animal no ha encontrado un refugio por sí mismo, guíalo suavemente hacia la habitación preparada. Puerta abierta, nunca encerrado." },
          { title: "Sonido ambiental", desc: "Música clásica, ruido blanco o el televisor a volumen medio. No compite con los petardos, pero sí difumina los estallidos." },
          { title: "Doble sujeción si hay que salir", desc: "Si un perro tiene que salir a hacer sus necesidades, collar + arnés + correa corta. Un tirón de pánico y un perro con solo collar desaparece en segundos." },
          { title: "Retira objetos peligrosos", desc: "En un episodio de pánico, un perro puede destrozar cristales, cables eléctricos, o tragarse objetos. Revisa el espacio." },
          { title: "Medicación solo con prescripción", desc: "Nunca administres ansiolíticos humanos ni medicación sin indicación veterinaria explícita para tu animal." }
        ]
      },
      {
        type: "split",
        reverse: true,
        left: {
          type: "text",
          title: "Gatos: el peligro invisible de los balcones",
          content: [
            "Cada noche de fuegos artificiales, decenas de gatos caen desde balcones y ventanas. El pánico les hace saltar a través de mosquiteras, cristales entreornados o barandillas que normalmente ignoran. El síndrome del gato paracaidista (high-rise syndrome) se multiplica en noches de pirotecnia.",
            "Si tienes gato, cierra absolutamente todas las ventanas y balcones antes de que empiece el ruido. No confíes en mosquiteras — un gato aterrorizado las revienta. Habilita escondites seguros en el interior: una caja con manta en el armario, espacio debajo de la cama, detrás del sofá. Feliway u otros difusores de feromonas pueden ayudar si se conectan 48 horas antes."
          ]
        },
        right: {
          type: "image",
          src: "https://images.pexels.com/photos/5958800/pexels-photo-5958800.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
          alt: "Gato escondido bajo la cama buscando refugio"
        }
      },
      {
        title: "LO QUE NUNCA DEBES HACER",
        type: "colorblock",
        color: "#E8412A",
        content: "Estos errores agravan el sufrimiento del animal y pueden provocar emergencias veterinarias o pérdidas irreversibles.",
        items: [
          { title: "Castigar o gritar", desc: "Nunca castigues a un animal por tener miedo. El miedo no es un comportamiento: es una respuesta fisiológica que el animal no controla." },
          { title: "Forzar la exposición al ruido", desc: "La idea de que 'se acostumbrará' es un mito peligroso. Cada exposición sin tratamiento profesional refuerza y cronifica el trauma." },
          { title: "Dejar al animal atado al exterior", desc: "Un animal atado en un balcón, terraza o patio durante la pirotecnia puede estrangularse, lesionarse gravemente o sufrir un colapso." },
          { title: "Medicar sin veterinario", desc: "Los ansiolíticos humanos pueden ser tóxicos para perros y gatos. Solo un veterinario puede prescribir la dosis y el fármaco correcto." },
          { title: "Esperar al último momento", desc: "Si preparas el refugio cuando ya suenan los petardos, tu animal ya está en estado de pánico. La preparación se hace horas — o días — antes." },
          { title: "Pasear en hora punta", desc: "Sacar al perro a las 00:00 del 1 de enero es exponerlo al pico máximo de explosiones. Los paseos se adelantan, siempre." }
        ]
      },
      {
        type: "pullquote",
        text: "La pirotecnia mata a más animales domésticos cada año que muchas emergencias naturales. No es un tema menor, es una emergencia silenciada que se repite cada fiesta patronal, cada Nochevieja, cada celebración.",
        attribution: "Fundación Affinity — Estudio sobre abandono y pérdida de animales 2025",
        color: "#2D3561"
      },
      {
        title: "ESPECÍFICO POR ANIMAL",
        type: "animals",
        animals: [
          {
            name: "PERROS",
            emoji: "",
            color: "#686af3",
            tips: [
              "Paseos adelantados con arnés antiescape (nunca solo collar)",
              "Refugio interior con manta familiar y juguete de morder",
              "Compañía tranquila — tu calma regula la suya",
              "Doble sujeción (collar + arnés) si hay que salir de emergencia",
              "Las razas ansiosas (border collie, pastor alemán, podenco) requieren atención especial"
            ]
          },
          {
            name: "GATOS",
            emoji: "",
            color: "#a88efb",
            tips: [
              "Cerrar TODAS las ventanas y balcones sin excepción",
              "No forzar a salir del escondite elegido",
              "Feliway conectado 48h antes si es posible",
              "Cuidado con las mosquiteras: un gato en pánico las revienta",
              "Vigilar los días posteriores — el estrés en gatos se manifiesta con retraso"
            ]
          },
          {
            name: "PEQUEÑOS MAMÍFEROS Y AVES",
            emoji: "",
            color: "#8B9A2D",
            tips: [
              "Jaula o recinto en la habitación más interior de la casa",
              "Cubrir parcialmente con tela gruesa (reduce ruido y destellos)",
              "Las aves son extremadamente sensibles: un susto fuerte puede causar paro cardíaco",
              "Mantener rutina de alimentación y agua",
              "Conejos y hurones: vigilar inapetencia los días siguientes"
            ]
          }
        ]
      },
      {
        title: "KIT BÁSICO NOCHE DE FUEGOS",
        type: "checklist",
        items: [
          { text: "Microchip registrado con teléfono actual", essential: true },
          { text: "Arnés antiescape y correa corta en buen estado", essential: true },
          { text: "Refugio interior preparado con manta, agua y juguete", essential: true },
          { text: "Ventanas, balcones y persianas cerrados", essential: true },
          { text: "Música o ruido ambiental listo para encender", essential: false },
          { text: "Teléfono de urgencias veterinarias guardado en el móvil", essential: true },
          { text: "Feliway u otro difusor de feromonas (gatos)", essential: false },
          { text: "Foto reciente del animal en el móvil (por si se pierde)", essential: true }
        ]
      },
      {
        type: "image",
        src: "https://images.unsplash.com/photo-1727532727212-a810e45fb804?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxzY2FyZWQlMjBkb2clMjBoaWRpbmclMjBibGFua2V0JTIwYW54aWV0eXxlbnwwfHx8fDE3ODI0MTExMzV8MA&ixlib=rb-4.1.0&q=85",
        alt: "Perro escondiéndose bajo un mueble por el ruido de los petardos",
        caption: "REFUGIO INSTINTIVO — Los animales buscan el espacio más oscuro y protegido que encuentren. Respeta su elección. No lo saques de ahí.",
        wide: false
      },
      {
        title: "CUÁNDO IR AL VETERINARIO DE URGENCIA",
        type: "callout",
        content: "Acude de urgencia inmediata si observas: dificultad respiratoria, colapso, traumatismo por caída o golpe, hemorragia, convulsiones, o una reacción de pánico que no remite pasadas dos horas. Si tu animal tiene historial de fobia al ruido o enfermedades cardíacas, planifica la prevención con tu veterinario semanas antes de las fiestas. La anticipación salva vidas.",
        cta: { label: "IR A PREPARACIÓN DE EMERGENCIAS", href: "/prepara" }
      }
    ],
    downloadables: [
      {
        title: "FAQs: Pirotecnia y animales",
        desc: "Preguntas frecuentes sobre pirotecnia y protección animal. Material descargable para compartir con vecinos y comunidades.",
        url: "https://customer-assets.emergentagent.com/job_c6a171c0-2fac-4c79-82dc-d458ca8ad670/artifacts/nuxn9cty_Emergencia%20Animal_FAQs_Pirotecnia.pdf",
        type: "PDF",
        size: "FAQ completa",
      }
    ],
  }
];

export default BLOG_ARTICLES;
